import React from 'react';
import { styled } from '@linaria/react';
import { Pause, Play } from 'lucide-react';

import useHasHydrated from '@/hooks/use-has-hydrated';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import Spinner from '@/components/Spinner';
import ButtonPlayful from '@/components/ButtonPlayful';

function TwitterLikeAnimation() {
  const [isPlaying, setIsPlaying] = React.useState(true);
  const hasHydrated = useHasHydrated();
  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    setIsPlaying(!prefersReducedMotion);
  }, [prefersReducedMotion]);

  return (
    <Wrapper>
      <SpriteWrapper>
        <Sprite
          draggable={false}
          alt="A heart goes from gray to red, swelling and spraying particles in all directions"
          src="/images/sprites/twitter-like-sprite.webp"
          style={{
            opacity: hasHydrated ? 1 : 0,
            animationPlayState: isPlaying ? 'running' : 'paused',
          }}
        />
        {!hasHydrated && (
          <LoadingWrapper>
            <Spinner />
          </LoadingWrapper>
        )}
      </SpriteWrapper>
      <Button
        Icon={isPlaying ? Pause : Play}
        onClick={() => setIsPlaying(!isPlaying)}
        textAlign="true-center"
      >
        {isPlaying ? 'Pause' : 'Play'}
      </Button>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 20rem;
  margin-inline: auto;
  margin-bottom: 2rem;
  padding: 16px;
  border: 1px solid var(--color-gray-300);
  border-radius: 16px;

  @media (max-width: 30rem) {
    width: auto;
    margin-inline: calc(var(--viewport-padding) * -1);
  }
`;

const SpriteWrapper = styled.div`
  position: relative;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Sprite = styled.img`
  display: block;
  width: 100px;
  height: 100px;
  object-fit: cover;
  object-position: 0% 0%;
  user-select: none;
  transition: opacity 300ms;
  image-rendering: optimizeSpeed;
  image-rendering: -moz-crisp-edges;
  image-rendering: -o-crisp-edges;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: pixelated;
  image-rendering: optimize-contrast;
  -ms-interpolation-mode: nearest-neighbor;

  @keyframes play {
    from {
      object-position: 0% 0%;
    }
    to {
      object-position: 100% 0%;
    }
  }

  animation: play 967ms steps(28) infinite;
`;

const Button = styled(ButtonPlayful)`
  --pb-background-color: var(--color-gray-900);
  --pb-highlight-color: var(--color-gray-800);
  width: 100%;

  html[data-color-mode='dark'] & {
    --pb-background-color: transparent;
    --pb-highlight-color: hsl(210deg 15% 80% / 0.2);
    --selection-background-color: var(--pb-highlight-color);
    --selection-text-color: white;

    outline: 1px solid var(--color-gray-300);
    color: white;
  }
`;

export default TwitterLikeAnimation;
